import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import useService from "../hooks/useService.ts";
import { Cliente } from "../models/Cliente";
import { DtoUsuarioRegrasString } from "../models/DtoUsuarioRegrasString.ts";
import { DtoVeiculoRetorno } from "../models/DtoVeiculoRetorno.js";
import { Embalagem } from "../models/Embalagem";
import { EmbarqueTemplate } from "../models/EmbarqueTemplate.ts";
import { EmpresaSub } from "../models/EmpresaSub";
import { Filial } from "../models/Filial";
import { MeioPagamento } from "../models/MeioPagamento";
import { Mercadoria } from "../models/Mercadoria";
import { Motivo } from "../models/Motivo";
import { Motorista } from "../models/Motorista.ts";
import { Ocorrencias } from "../models/Ocorrencias.js";
import { Proprietario } from "../models/Proprietario.ts";
import { RetornoPedidoDTO } from "../models/RetornoPedidoDTO.js";
import { Status } from "../models/Status";
import { SubTipoOcorrencia } from "../models/SubTipoOcorrencia";
import { TipoCarroceria } from "../models/TipoCarroceria.ts";
import { TipoOcorrencia } from "../models/TipoOcorrencia";
import { TipoVeiculo } from "../models/TipoVeiculo.ts";
import { UnidadeEmbarque } from "../models/UnidadeEmbarque";
import { getCidadeEstadoComSigla } from "../services/ModuloGerenciador/CidadeEstado";
import { getCliente } from "../services/ModuloGerenciador/Cliente";
import { getEmbalagem } from "../services/ModuloGerenciador/Embalagem.js";
import { getEmpresaSub } from "../services/ModuloGerenciador/EmpresaSub.js";
import { getFilial } from "../services/ModuloGerenciador/Filial.js";
import {
    getEmbarqueDependenciasCadastroData,
} from "../services/ModuloGerenciador/Geral.ts";
import { getMeioPagamento } from "../services/ModuloGerenciador/MeioPagamento.js";
import { getMercadoria } from "../services/ModuloGerenciador/Mercadoria.js";
import { getMotivo } from "../services/ModuloGerenciador/Motivo.js";
import { getMotorista } from "../services/ModuloGerenciador/Motorista.ts";
import { getOcorrencia } from "../services/ModuloGerenciador/Ocorrencia.js";
import { getPedido } from "../services/ModuloGerenciador/Pedido.js";
import { getProprietario } from "../services/ModuloGerenciador/Proprietario.js";
import { getStatus } from "../services/ModuloGerenciador/Status";
import { getSubTipoOcorrencia } from "../services/ModuloGerenciador/SubTipoOcorrencia.js";
import { getTipoCarroceria } from "../services/ModuloGerenciador/TipoCarroceria.js";
import { getTipoOcorrencia } from "../services/ModuloGerenciador/TipoOcorrencia.js";
import { getTipoVeiculo } from "../services/ModuloGerenciador/TipoVeiculo.js";
import { getUnidadeEmbarque } from "../services/ModuloGerenciador/UnidadeEmbarque";
import { getVeiculo } from "../services/ModuloGerenciador/Veiculo.js";

interface EmbarqueDependenciasContextType {
    unidadeEmbarque: UnidadeEmbarque[];
    empresaSub: EmpresaSub[];
    filial: Filial[];
    pedido: RetornoPedidoDTO[];
    veiculo: DtoVeiculoRetorno[];
    embalagem: Embalagem[];
    status: Status[];
    meioPagamento: MeioPagamento[];
    motivo: Motivo[];
    mercadoria: Mercadoria[];
    clientes: Cliente[];
    ocorrencias: Ocorrencias[];
    tipoOcorrencias: TipoOcorrencia[];
    subTipoOcorrencias: SubTipoOcorrencia[];
    embarqueTemplate: EmbarqueTemplate[];
    motorista: Motorista[];
    proprietario: Proprietario[];
    placaCarreta: { title: string, obj: DtoVeiculoRetorno }[];
    placaCavalo: { title: string, obj: DtoVeiculoRetorno }[];
    tipoCarroceria: TipoCarroceria[];
    tipoVeiculo: TipoVeiculo[];
    buscandoEmbarqueEDependencias: boolean;
    fetchEmbarqueEDependencias: (token: string) => void;
    fetchUnidadeEmbarque: (token: string) => void;
    fetchEmpresaSub: (token: string) => void;
    fetchFilial: (token: string) => void;
    fetchPedidos: (token: string) => void;
    fetchVeiculos: (token: string) => void;
    fetchEmbalagens: (token: string) => void;
    fetchStatus: (token: string) => void;
    fetchMeiosPagamento: (token: string) => void;
    fetchMotivos: (token: string) => void;
    fetchMercadorias: (token: string) => void;
    fetchClientes: (token: string) => void;
    fetchOcorrencias: (token: string) => void;
    fetchTipoOcorrencias: (token: string) => void;
    fetchSubTipoOcorrencias: (token: string) => void;
}

interface EmbarqueDependenciasProviderProps {
    children: ReactNode;
}

const EmbarqueDependenciasContext = createContext<
    EmbarqueDependenciasContextType | undefined
>(undefined);

export const EmbarqueDependenciasProvider = ({
                                                 children,
                                             }: EmbarqueDependenciasProviderProps) => {
    const [unidadeEmbarque, setUnidadeEmbarque] = useState<UnidadeEmbarque[]>([]);
    const [empresaSub, setEmpresaSub] = useState<EmpresaSub[]>([]);
    const [filial, setFilial] = useState<Filial[]>([]);
    const [usuarioGerens, setUsuarioGerens] = useState<DtoUsuarioRegrasString[]>(
        []
    );
    const [pedido, setPedido] = useState<RetornoPedidoDTO[]>([]);
    const [veiculo, setVeiculo] = useState<DtoVeiculoRetorno[]>([]);
    const [embalagem, setEmbalagem] = useState<Embalagem[]>([]);
    const [status, setStatus] = useState<Status[]>([]);
    const [meioPagamento, setMeioPagamento] = useState<MeioPagamento[]>([]);
    const [motivo, setMotivo] = useState<Motivo[]>([]);
    const [mercadoria, setMercadoria] = useState<Mercadoria[]>([]);
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [cidade, setCidade] = useState<Cliente[]>([]);
    const [ocorrencias, setOcorrencias] = useState<Ocorrencias[]>([]);
    const [tipoOcorrencias, setTipoOcorrencias] = useState<TipoOcorrencia[]>([]);
    const [subTipoOcorrencias, setSubTipoOcorrencias] = useState<
        SubTipoOcorrencia[]
    >([]);
    const [embarqueTemplate, setEmbarqueTemplate] = useState<EmbarqueTemplate[]>(
        []
    );
    const [motorista, setMotorista] = useState<Motorista[]>([]);
    const [proprietario, setProprietario] = useState<Proprietario[]>([]);
    const [placaCarreta, setPlacaCarreta] = useState<{ title: string, obj: DtoVeiculoRetorno }[]>([]);
    const [placaCavalo, setPlacaCavalo] = useState<{ title: string, obj: DtoVeiculoRetorno }[]>([]);
    const [tipoCarroceria, setTipoCarroceria] = useState<TipoCarroceria[]>([]);
    const [tipoVeiculo, setTipoVeiculo] = useState<TipoVeiculo[]>([]);

    const [buscandoEmbarqueEDependencias, buscarEmbarqueEDependencias] =
        useService(getEmbarqueDependenciasCadastroData, {
            autoStart: false,
            onData: (response) => {
                setUnidadeEmbarque(response.data.unidadeEmbarque ?? []);
                setEmpresaSub(response.data.empresaSub ?? []);
                setUsuarioGerens(response.data.usuarioGerens ?? []);
                setFilial(response.data.filial ?? []);
                setEmbalagem(response.data.embalagem ?? []);
                setStatus(response.data.status ?? []);
                setMeioPagamento(response.data.meioPagamento ?? []);
                setMotivo(response.data.motivo ?? []);
                setMercadoria(response.data.mercadoria ?? []);
                setOcorrencias(response.data.ocorrencias ?? []);
                setTipoOcorrencias(response.data.tipoOcorrencias ?? []);
                setSubTipoOcorrencias(response.data.subTipoOcorrencias ?? []);
                setEmbarqueTemplate(response.data.embarqueTemplete ?? []);
            },
        });

    const [, buscarProprietario] = useService(
        (token: string) => getProprietario(token),
        {
            autoStart: false,
            onData: (response) => {
                setProprietario(response.data);
            },
        }
    );

    const [, buscarMotorista] = useService(
        (token: string) => getMotorista({token}),
        {
            autoStart: false,
            onData: (response: any) => {
                setMotorista(response.data);
            },
        }
    );

    const [, buscarTipoVeiculo] = useService(
        (token: string) => getTipoVeiculo(token),
        {
            autoStart: false,
            onData: (response) => {
                setTipoVeiculo(response.data);
            },
        }
    );

    const [, buscarTipoCarroceria] = useService(
        (token: string) => getTipoCarroceria(token),
        {
            autoStart: false,
            onData: (response) => {
                setTipoCarroceria(response.data);
            },
        }
    );

    const [, buscarUnidadeEmbarque] = useService(
        (token: string) => getUnidadeEmbarque(token),
        {
            autoStart: false,
            onData: (response) => {
                setUnidadeEmbarque(response.data);
            },
        }
    );

    const [, buscarEmpresaSub] = useService(
        (token: string) => getEmpresaSub(token),
        {
            autoStart: false,
            onData: (response) => {
                setEmpresaSub(response.data);
            },
        }
    );

    const [, buscarFilial] = useService((token: string) => getFilial(token), {
        autoStart: false,
        onData: (response) => {
            setFilial(response.data);
        },
    });

    const [, buscarPedidos] = useService((token: string) => getPedido(token), {
        autoStart: false,
        onData: (response) => {
            setPedido(response.data);
        },
    });

    const [, buscarVeiculos] = useService(
        (token: string) => getVeiculo({token}),
        {
            autoStart: false,
            onData: (response) => {
                setVeiculo(response.data);
            },
        }
    );

    const [, buscarEmbalagens] = useService(
        (token: string) => getEmbalagem(token),
        {
            autoStart: false,
            onData: (response) => {
                setEmbalagem(response.data);
            },
        }
    );

    const [, buscarStatus] = useService((token: string) => getStatus(token), {
        autoStart: false,
        onData: (response) => {
            setStatus(response.data);
        },
    });

    const [, buscarMeiosPagamento] = useService(
        (token: string) => getMeioPagamento(token),
        {
            autoStart: false,
            onData: (response) => {
                setMeioPagamento(response.data);
            },
        }
    );

    const [, buscarMotivos] = useService((token: string) => getMotivo(token), {
        autoStart: false,
        onData: (response) => {
            setMotivo(response.data);
        },
    });

    const [, buscarMercadorias] = useService(
        (token: string) => getMercadoria(token),
        {
            autoStart: false,
            onData: (response) => {
                setMercadoria(response.data);
            },
        }
    );

    const [, buscarClientes] = useService((token: string) => getCliente(token), {
        autoStart: false,
        onData: (response) => {
            setClientes(response.data);
        },
    });

    const [, buscarOcorrencias] = useService(
        (token: string) => getOcorrencia(token),
        {
            autoStart: false,
            onData: (response) => {
                setOcorrencias(response.data);
            },
        }
    );

    const [, buscarTipoOcorrencias] = useService(
        (token: string) => getTipoOcorrencia(token),
        {
            autoStart: false,
            onData: (response) => {
                setTipoOcorrencias(response.data);
            },
        }
    );

    const [, buscarSubTipoOcorrencias] = useService(
        (token: string) => getSubTipoOcorrencia(token),
        {
            autoStart: false,
            onData: (response) => {
                setSubTipoOcorrencias(response.data);
            },
        }
    );

    const [, buscarCidade] = useService((token: string) => getCidadeEstadoComSigla(token), {
            autoStart: false,
            onData: (response) => {
                setCidade(response.data);
            },
        }
    );

    const fetchEmbarqueEDependencias = useCallback(
        (token: string) => {
            buscarEmbarqueEDependencias({token});
            buscarProprietario(token);
            buscarMotorista(token);
            buscarTipoVeiculo(token);
            buscarTipoCarroceria(token);
            buscarVeiculos(token);
            buscarClientes(token);
            buscarPedidos(token);
            buscarCidade(token);
        },
        [
            buscarEmbarqueEDependencias,
            buscarProprietario,
            buscarMotorista,
            buscarTipoVeiculo,
            buscarTipoCarroceria,
            buscarVeiculos,
            buscarClientes,
            buscarPedidos,
            buscarCidade,
        ]
    );

    const fetchUnidadeEmbarque = useCallback(
        (token: string) => {
            buscarUnidadeEmbarque(token);
        },
        [buscarUnidadeEmbarque]
    );

    const fetchEmpresaSub = useCallback(
        (token: string) => {
            buscarEmpresaSub(token);
        },
        [buscarEmpresaSub]
    );

    const fetchFilial = useCallback(
        (token: string) => {
            buscarFilial(token);
        },
        [buscarFilial]
    );

    const fetchPedidos = useCallback(
        (token: string) => {
            buscarPedidos(token);
        },
        [buscarPedidos]
    );

    const fetchVeiculos = useCallback(
        (token: string) => {
            buscarVeiculos(token);
        },
        [buscarVeiculos]
    );

    const fetchEmbalagens = useCallback(
        (token: string) => {
            buscarEmbalagens(token);
        },
        [buscarEmbalagens]
    );

    const fetchStatus = useCallback(
        (token: string) => {
            buscarStatus(token);
        },
        [buscarStatus]
    );

    const fetchMeiosPagamento = useCallback(
        (token: string) => {
            buscarMeiosPagamento(token);
        },
        [buscarMeiosPagamento]
    );

    const fetchMotivos = useCallback(
        (token: string) => {
            buscarMotivos(token);
        },
        [buscarMotivos]
    );

    const fetchMercadorias = useCallback(
        (token: string) => {
            buscarMercadorias(token);
        },
        [buscarMercadorias]
    );

    const fetchClientes = useCallback(
        (token: string) => {
            buscarClientes(token);
        },
        [buscarClientes]
    );

    const fetchOcorrencias = useCallback(
        (token: string) => {
            buscarOcorrencias(token);
        },
        [buscarOcorrencias]
    );

    const fetchTipoOcorrencias = useCallback(
        (token: string) => {
            buscarTipoOcorrencias(token);
        },
        [buscarTipoOcorrencias]
    );

    const fetchSubTipoOcorrencias = useCallback(
        (token: string) => {
            buscarSubTipoOcorrencias(token);
        },
        [buscarSubTipoOcorrencias]
    );

    const fetchProprietario = useCallback(
        (token: string) => {
            buscarProprietario(token);
        },
        [buscarProprietario]
    );

    const fetchMotorista = useCallback(
        (token: string) => {
            buscarMotorista(token);
        },
        [buscarMotorista]
    );

    const fetchCidade = useCallback(
        (token: string) => {
            buscarCidade(token);
        },
        [buscarCidade]
    );

    useEffect(() => {
        if (!veiculo) return;

        const novasPlacasCarreta: { title: string, obj: DtoVeiculoRetorno }[] = [];
        const novasPlacasCavalo: { title: string, obj: DtoVeiculoRetorno }[] = [];
        const carretaSet = new Set<string>();
        const cavaloSet = new Set<string>();

        for (const item of veiculo) {
            if (item.carreta?.placa && !carretaSet.has(item.carreta.placa)) {
                carretaSet.add(item.carreta.placa);
                novasPlacasCarreta.push({
                    title: `${item.carreta.placa} - ${item.proprietarioVeiculo?.nome ?? ""}`,
                    obj: item,
                });
            }
            if (item.caminhao?.placa && !cavaloSet.has(item.caminhao.placa)) {
                cavaloSet.add(item.caminhao.placa);
                novasPlacasCavalo.push({
                    title: `${item.caminhao?.placa} - ${item.proprietarioVeiculo?.nome ?? ""}`,
                    obj: item,
                });
            }
        }

        novasPlacasCarreta.sort((a, b) => a.title.localeCompare(b.title));
        novasPlacasCavalo.sort((a, b) => a.title.localeCompare(b.title));

        setPlacaCarreta(novasPlacasCarreta);
        setPlacaCavalo(novasPlacasCavalo);
    }, [veiculo]);

    const contextValue = useMemo(
        () => ({
            unidadeEmbarque,
            empresaSub,
            usuarioGerens,
            filial,
            pedido,
            veiculo,
            embalagem,
            status,
            meioPagamento,
            motivo,
            mercadoria,
            clientes,
            ocorrencias,
            tipoOcorrencias,
            subTipoOcorrencias,
            motorista,
            proprietario,
            placaCarreta,
            placaCavalo,
            tipoCarroceria,
            tipoVeiculo,
            buscandoEmbarqueEDependencias,
            embarqueTemplate,
            cidade,
            fetchEmbarqueEDependencias,
            fetchUnidadeEmbarque,
            fetchEmpresaSub,
            fetchFilial,
            fetchPedidos,
            fetchVeiculos,
            fetchEmbalagens,
            fetchStatus,
            fetchMeiosPagamento,
            fetchMotivos,
            fetchMercadorias,
            fetchClientes,
            fetchOcorrencias,
            fetchTipoOcorrencias,
            fetchSubTipoOcorrencias,
            fetchProprietario,
            fetchMotorista,
            fetchCidade,
        }),
        [
            unidadeEmbarque,
            empresaSub,
            usuarioGerens,
            filial,
            pedido,
            veiculo,
            embalagem,
            status,
            meioPagamento,
            motivo,
            mercadoria,
            clientes,
            cidade,
            ocorrencias,
            embarqueTemplate,
            tipoOcorrencias,
            subTipoOcorrencias,
            motorista,
            proprietario,
            placaCarreta,
            placaCavalo,
            tipoCarroceria,
            tipoVeiculo,
            buscandoEmbarqueEDependencias,
            fetchEmbarqueEDependencias,
            fetchUnidadeEmbarque,
            fetchEmpresaSub,
            fetchFilial,
            fetchPedidos,
            fetchVeiculos,
            fetchCidade,
            fetchEmbalagens,
            fetchStatus,
            fetchMeiosPagamento,
            fetchMotivos,
            fetchMercadorias,
            fetchClientes,
            fetchOcorrencias,
            fetchTipoOcorrencias,
            fetchSubTipoOcorrencias,
            fetchProprietario,
            fetchMotorista,
        ]
    );

    return (
        <EmbarqueDependenciasContext.Provider value={contextValue}>
            {children}
        </EmbarqueDependenciasContext.Provider>
    );
};

export const useEmbarqueDependencias = () => {
    const context = useContext(EmbarqueDependenciasContext);
    if (!context) {
        throw new Error(
            "useEmbarqueDependencias must be used within an EmbarqueDependenciasProvider"
        );
    }
    return context;
};
